import {Cell} from "@tanstack/react-table";
import {Dispatch, SetStateAction, useState} from "react";
import {FaRegClock} from "react-icons/fa6";

import {AssistantEmptyStateCta} from "@/components/assistant/AssistantEmptyStateCta.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {TwoLineShortDateTimeUser} from "@/components/datetime/TwoLineShortDateTimeUser.tsx";
import {MoreList} from "@/components/moreList/MoreList.tsx";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {TableEmptyStateCta} from "@/components/table/subComponents/TableEmptyStateCta/TableEmptyStateCta.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {CHECK_LABEL_MAX_LENGTH, cropWithEllipsis} from "@/lib/displayUtils.ts";
import {durationSecFn, roundDurationSecs} from "@/lib/format.tsx";
import {AnalyzerSource} from "@/lib/queries/analyzerRuns.ts";
import {Alert, ALERT_STATUS_TO_PILL_STATUS, GlobalAlertState} from "@/lib/types/alerting.ts";
import {CheckTypeDisplay} from "@/lib/types/checks.ts";
import {AlertDetailsModal} from "@/pages/alerting/components/details/AlertDetailsModal.tsx";
import {AlertActions} from "@/pages/alerting/components/list/AlertActions.tsx";
import {ChecksTags} from "@/pages/checks/list/components/CheckTags.tsx";

type AlertsTableProps = {
  alerts: GlobalAlertState[];
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  enableSorting?: boolean;
  className?: string;
  dataTestId?: string;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  isFiltered?: boolean;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: Dispatch<SetStateAction<Record<string, boolean>>>;
  actionBar?: React.ReactNode;
};

export const AlertsTable = ({
  alerts,
  sortColumn,
  sortDirection,
  onSortingChange,
  enableSorting = true,
  className,
  dataTestId,
  pagination,
  totalRows,
  updatePagination,
  isFiltered,
  rowSelection,
  setRowSelection,
  actionBar,
}: AlertsTableProps) => {
  const {user} = useCurrentUserContext();
  const [showAlertDetails, setShowAlertDetails] = useState(false);
  const [alertDetailsId, setAlertDetailsId] = useState<number | undefined>(undefined);

  const onClickDrawerTrigger = (_: React.MouseEvent, cell: Cell<GlobalAlertState, unknown> | undefined) => {
    if (cell) {
      setAlertDetailsId(cell.row.original.id);
      setShowAlertDetails(true);
    }
  };

  const specs: ColumnSpec<GlobalAlertState>[] = [
    {
      fieldName: "service_name_lower",
      fieldTitle: "Name",
      enableSorting: enableSorting,
      meta: {
        headerClassName: "w-20",
        onClick: onClickDrawerTrigger,
      },
      accessor: (gas: GlobalAlertState) => {
        const {name, msp_address} = gas.service;
        const croppedAddress = cropWithEllipsis(msp_address, CHECK_LABEL_MAX_LENGTH);
        const addressTooltip = croppedAddress === msp_address ? undefined : msp_address;

        return (
          <>
            <div>{name}</div>
            <Tooltip tooltipContent={addressTooltip} delayMs={500}>
              <div className="pulse-color-gray-600">{croppedAddress}</div>
            </Tooltip>
            {gas.service_is_deleted && <div className="badge badge-pill rounded-pill pulse-bg-red-500">Deleted</div>}
          </>
        );
      },
    },
    {
      fieldName: "status",
      fieldTitle: "Status",
      enableSorting: enableSorting,
      meta: {
        headerClassName: "w-10",
        onClick: onClickDrawerTrigger,
      },
      accessor: ({status}: GlobalAlertState) => (
        <StatusPill type="incident" variant="textAndIcon" status={ALERT_STATUS_TO_PILL_STATUS[status]} />
      ),
    },
    {
      fieldName: "service__monitoring_service_type",
      fieldTitle: "Type",
      meta: {
        headerClassName: "w-10",
        onClick: onClickDrawerTrigger,
      },
      accessor: (gas: GlobalAlertState) => CheckTypeDisplay[gas.service_monitoring_type],
    },
    {
      fieldName: "created_at",
      fieldTitle: "From",
      enableSorting: enableSorting,
      meta: {
        headerClassName: "w-12-5",
        onClick: onClickDrawerTrigger,
      },
      accessor: (gas: GlobalAlertState) => <TwoLineShortDateTimeUser date={gas.created_at.toString()} user={user!} />,
    },
    {
      fieldName: "up_at",
      fieldTitle: "To",
      enableSorting: enableSorting,
      meta: {
        headerClassName: "w-12-5",
        onClick: onClickDrawerTrigger,
      },
      accessor: (gas: GlobalAlertState) => (
        <>{gas.up_at ? <TwoLineShortDateTimeUser date={gas.up_at.toString()} user={user!} /> : "Ongoing"}</>
      ),
    },
    {
      fieldName: "duration_sortable",
      fieldTitle: "Duration",
      enableSorting: enableSorting,
      meta: {
        headerClassName: "w-10",
        onClick: onClickDrawerTrigger,
      },
      accessor: (gas: GlobalAlertState) => {
        const startDate = new Date(gas.created_at);
        const endDate = new Date(gas.up_at || new Date());
        return (
          <>
            <FaRegClock /> {durationSecFn(roundDurationSecs((endDate.getTime() - startDate.getTime()) / 1000))}
          </>
        );
      },
    },
    {
      fieldName: "tags",
      fieldTitle: "Tags",
      meta: {
        headerClassName: "w-10",
        onClick: onClickDrawerTrigger,
      },
      accessor: (gas) => <ChecksTags tags={gas.service.tags} />,
    },
    {
      fieldName: "locations",
      fieldTitle: "Locations",
      meta: {
        headerClassName: "w-10",
        onClick: onClickDrawerTrigger,
      },
      accessor: (gas: GlobalAlertState) => (
        <MoreList entries={gas.down_alerts} renderer={(gas: Alert) => <>{gas?.monitoring_server?.location}</>} />
      ),
    },
    {
      fieldName: "quick_actions",
      meta: {
        headerClassName: "w-5",
        columnClassName: "cursor-default",
      },
      accessor: (gas: GlobalAlertState) => <AlertActions gas={gas} />,
    },
  ];

  return (
    <>
      <Table
        className={className}
        rows={alerts}
        minWidth="800px"
        specs={specs}
        initialState={{columnPinning: {right: ["quick_actions"]}}}
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        tableRowClassName="cursor-pointer"
        pagination={pagination}
        totalRows={totalRows}
        updatePagination={updatePagination}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        actionBar={actionBar}
        getRowId={(row) => String(row.id)}
        noDataComponent={
          isFiltered ? (
            <TableEmptyStateCta title="No Alerts found matching the filter(s) above." />
          ) : (
            <AssistantEmptyStateCta
              title="You do not have any Alerts"
              assistantDescriptionIntro="Alerts appear here when your Checks detect downtime."
              source={AnalyzerSource.ALERTS_EMPTY_TABLE}
            />
          )
        }
        dataTestId={dataTestId}
      />
      <AlertDetailsModal
        id={alertDetailsId}
        show={showAlertDetails}
        hide={() => {
          setShowAlertDetails(false);
        }}
      />
    </>
  );
};
